import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { WSStatus } from '@/lib/wsClient';

interface ConnectionPillProps {
  status: WSStatus;
}

const styleMap: Record<WSStatus, { wrap: string; dot: string; ring: string }> = {
  idle: {
    wrap: 'border-border/60 bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground',
    ring: '',
  },
  connecting: {
    wrap: 'border-warning/40 bg-warning/10 text-warning',
    dot: 'bg-warning',
    ring: 'animate-heartbeat',
  },
  live: {
    wrap: 'border-success/40 bg-success/10 text-success',
    dot: 'bg-success',
    ring: 'animate-heartbeat',
  },
  reconnecting: {
    wrap: 'border-warning/40 bg-warning/10 text-warning',
    dot: 'bg-warning',
    ring: 'animate-heartbeat',
  },
  offline: {
    wrap: 'border-destructive/40 bg-destructive/10 text-destructive',
    dot: 'bg-destructive',
    ring: '',
  },
};

export function ConnectionPill({ status }: ConnectionPillProps) {
  const { t } = useTranslation();
  const labelMap: Record<WSStatus, string> = {
    idle: '—',
    connecting: t('connection.connecting'),
    live: t('connection.live'),
    reconnecting: t('connection.reconnecting'),
    offline: t('connection.offline'),
  };
  const s = styleMap[status];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]',
        s.wrap,
      )}
    >
      <span className="relative flex h-2 w-2 items-center justify-center">
        <span className={cn('absolute h-2 w-2 rounded-full opacity-60', s.dot, s.ring)} />
        <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} />
      </span>
      {labelMap[status]}
    </div>
  );
}
